import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles, User, MessageSquare } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STORAGE_KEY = "titan_wingman_session";
const HISTORY_KEY = "titan_wingman_history";
const VISITOR_KEY = "titan_wingman_visitor";
const INTAKE_KEY = "titan_wingman_intake_state"; // "submitted" | "skipped"

function newSession() {
  return `wm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function newMsgId() {
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const INTRO_MSG = {
  id: "intro",
  role: "assistant",
  content:
    "Akwaaba! 👋 I'm Akua Titan, your 24/7 rental assistant at Titan Car Rentals. May I have your name and either a phone number or an email — just in case we get disconnected? Then tell me: what kind of car or trip are you planning?",
};

export default function WingmanWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([INTRO_MSG]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const [visitor, setVisitor] = useState({ name: "", email: "", phone: "" });
  const [intakeState, setIntakeState] = useState("pending"); // pending | submitted | skipped
  const [intakeForm, setIntakeForm] = useState({ name: "", email: "", phone: "" });
  const [intakeError, setIntakeError] = useState("");

  const scrollRef = useRef(null);

  useEffect(() => {
    let sid = localStorage.getItem(STORAGE_KEY);
    if (!sid) {
      sid = newSession();
      localStorage.setItem(STORAGE_KEY, sid);
    }
    setSessionId(sid);

    const cached = localStorage.getItem(HISTORY_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // backfill ids onto historical messages so React keys stay stable
          setMessages(parsed.map((m) => (m.id ? m : { ...m, id: newMsgId() })));
        }
      } catch (e) {
        console.warn("Wingman: could not parse cached history", e);
      }
    }
    const v = localStorage.getItem(VISITOR_KEY);
    if (v) {
      try {
        setVisitor(JSON.parse(v));
      } catch (e) {
        console.warn("Wingman: could not parse cached visitor", e);
      }
    }
    const i = localStorage.getItem(INTAKE_KEY);
    if (i === "submitted" || i === "skipped") setIntakeState(i);
  }, []);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, sending, intakeState]);

  useEffect(() => {
    if (messages.length > 1) {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-50)));
    }
  }, [messages]);

  const submitIntake = (e) => {
    e?.preventDefault();
    setIntakeError("");
    const name = intakeForm.name.trim();
    const email = intakeForm.email.trim();
    const phone = intakeForm.phone.trim();
    if (!name) { setIntakeError("Please share your name."); return; }
    if (!email && !phone) { setIntakeError("Please share either an email or a phone number."); return; }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setIntakeError("That email looks off — please check it."); return; }

    const v = { name, email, phone };
    setVisitor(v);
    localStorage.setItem(VISITOR_KEY, JSON.stringify(v));
    setIntakeState("submitted");
    localStorage.setItem(INTAKE_KEY, "submitted");

    setMessages((m) => [
      ...m,
      { id: newMsgId(), role: "assistant", content: `Lovely to meet you, ${name.split(" ")[0]}! 🙏 Your details are saved. So — what car or trip can I help you with today?` },
    ]);
  };

  const skipIntake = () => {
    setIntakeState("skipped");
    localStorage.setItem(INTAKE_KEY, "skipped");
    setMessages((m) => [
      ...m,
      { id: newMsgId(), role: "assistant", content: "No worries — we can chat first. What kind of car or trip are you planning?" },
    ]);
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { id: newMsgId(), role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/wingman/chat", {
        session_id: sessionId,
        message: text,
        visitor_name: visitor.name || null,
        visitor_email: visitor.email || null,
        visitor_phone: visitor.phone || null,
      });
      setMessages((m) => [...m, { id: newMsgId(), role: "assistant", content: data.reply }]);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || "Akua is taking a quick break. Please try again.";
      setMessages((m) => [...m, { id: newMsgId(), role: "assistant", content: detail, isError: true }]);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    const sid = newSession();
    setSessionId(sid);
    localStorage.setItem(STORAGE_KEY, sid);
    localStorage.removeItem(HISTORY_KEY);
    localStorage.removeItem(INTAKE_KEY);
    localStorage.removeItem(VISITOR_KEY);
    setMessages([INTRO_MSG]);
    setVisitor({ name: "", email: "", phone: "" });
    setIntakeForm({ name: "", email: "", phone: "" });
    setIntakeState("pending");
    setIntakeError("");
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 left-5 z-50 group flex items-center gap-3 bg-[#e63946] text-white py-3 pl-4 pr-5 shadow-lg hover:shadow-2xl hover:-translate-y-0.5 transition-all rounded-sm"
          data-testid="wingman-launcher"
          aria-label="Open Akua Titan chat"
        >
          <div className="relative">
            <MessageCircle size={20} />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-400 rounded-full ring-2 ring-[#e63946]" />
          </div>
          <span className="font-bold text-sm uppercase tracking-wider hidden md:inline">Chat with Akua</span>
          <span className="font-bold text-sm uppercase tracking-wider md:hidden">Chat</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-[400px] h-[calc(100vh-7rem)] sm:h-[620px] bg-white border border-gray-200 shadow-2xl flex flex-col"
          data-testid="wingman-panel"
        >
          {/* Header */}
          <div className="bg-[#0d2040] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#e63946] flex items-center justify-center">
                <Sparkles size={18} className="text-white" />
              </div>
              <div>
                <div className="font-display text-lg font-bold leading-none">Akua Titan</div>
                <div className="text-[0.65rem] text-white/60 uppercase tracking-widest mt-1 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block" /> Online 24/7 · Wingman
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={reset}
                className="text-[0.65rem] uppercase tracking-widest text-white/60 hover:text-white px-2 py-1"
                data-testid="wingman-reset"
                title="Start a fresh chat"
              >
                Reset
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 hover:bg-white/10"
                data-testid="wingman-close"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f7f8fa]" data-testid="wingman-messages">
            {/* WhatsApp handoff banner — only after intake captured contact info */}
            {intakeState === "submitted" && (visitor.phone || visitor.email) && (
              <a
                href={(() => {
                  const lastUserMsg = [...messages].reverse().find((m) => m.role === "user")?.content || "";
                  const firstName = (visitor.name || "").split(" ")[0] || "there";
                  const topic = lastUserMsg.slice(0, 140) || "a rental";
                  const msg = `Hi Titan! This is ${firstName}. I was just chatting with Akua about: "${topic}". Can we continue here?`;
                  return `https://wa.me/233244945582?text=${encodeURIComponent(msg)}`;
                })()}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center justify-between gap-3 bg-[#25D366] hover:bg-[#1da851] text-white px-3.5 py-2.5 transition-colors"
                data-testid="wingman-whatsapp-handoff"
              >
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <MessageSquare size={16} />
                  Click for WhatsApp Chat
                </span>
                <span className="text-[0.65rem] uppercase tracking-widest opacity-90">24/7</span>
              </a>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#0d2040] text-white"
                      : m.isError
                      ? "bg-red-50 border border-red-200 text-red-700"
                      : "bg-white border border-gray-200 text-[#0d2040]"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {/* Intake form — shown immediately after Akua's intro */}
            {intakeState === "pending" && (
              <div className="bg-white border border-[#e63946]/30 p-3.5 space-y-2.5" data-testid="wingman-intake">
                <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-widest text-[#e63946] font-bold">
                  <User size={12} /> Quick intro
                </div>
                <form onSubmit={submitIntake} noValidate className="space-y-2">
                  <input
                    type="text"
                    placeholder="Your name *"
                    value={intakeForm.name}
                    onChange={(e) => setIntakeForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:border-[#e63946]"
                    data-testid="wingman-intake-name"
                    autoFocus
                  />
                  <input
                    type="tel"
                    placeholder="Phone (e.g. 024 494 5582)"
                    value={intakeForm.phone}
                    onChange={(e) => setIntakeForm((f) => ({ ...f, phone: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:border-[#e63946]"
                    data-testid="wingman-intake-phone"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={intakeForm.email}
                    onChange={(e) => setIntakeForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:border-[#e63946]"
                    data-testid="wingman-intake-email"
                  />
                  <div className="text-[0.65rem] text-gray-500">Name + either phone or email. We use this only if we get disconnected.</div>
                  {intakeError && (
                    <div className="text-xs text-red-600" data-testid="wingman-intake-error">{intakeError}</div>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="submit"
                      className="bg-[#e63946] text-white text-xs uppercase tracking-wider font-bold px-3 py-2 hover:bg-[#c5202e]"
                      data-testid="wingman-intake-submit"
                    >
                      Continue
                    </button>
                    <button
                      type="button"
                      onClick={skipIntake}
                      className="text-xs uppercase tracking-wider text-gray-500 hover:text-[#0d2040] px-2 py-2"
                      data-testid="wingman-intake-skip"
                    >
                      Skip
                    </button>
                  </div>
                </form>
              </div>
            )}

            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 px-3.5 py-2.5 flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 size={14} className="animate-spin" /> Akua is typing…
                </div>
              </div>
            )}
          </div>

          {/* Quick actions (only before any user message and intake handled) */}
          {messages.filter((m) => m.role === "user").length === 0 && intakeState !== "pending" && (
            <div className="px-4 pb-2 flex flex-wrap gap-2 bg-[#f7f8fa] border-t border-gray-100">
              {[
                "What cars do you have?",
                "How much for 3 days to Kumasi?",
                "Do you do airport pickup?",
                "I need a car for a wedding",
              ].map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(q); }}
                  className="text-xs px-3 py-1.5 bg-white border border-gray-200 hover:border-[#e63946] hover:text-[#e63946] text-gray-700"
                  data-testid={`wingman-suggestion-${q.slice(0, 10).replace(/\s/g, "-")}`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={send} className="p-3 border-t border-gray-200 bg-white flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={intakeState === "pending" ? "Finish the quick intro above…" : "Ask Akua about cars, prices, dates…"}
              className="flex-1 px-3 py-2.5 text-sm border border-gray-300 focus:outline-none focus:border-[#e63946] disabled:bg-gray-50"
              disabled={sending || intakeState === "pending"}
              data-testid="wingman-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim() || intakeState === "pending"}
              className="bg-[#e63946] text-white px-4 py-2.5 hover:bg-[#c5202e] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="wingman-send"
              aria-label="Send"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}

